/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { filterByKeyword, sortByProp } from '@squiz/dxp-console-support';
import {
  Button,
  DIALOG_SIZE_SM_MD,
  DialogContentProps,
  DialogProps,
  EmptyState,
  ICON_INFO_WITH_LIST,
  ICON_INFO_WITH_SEARCH,
  SPINNER_TYPE_REGULAR,
  SearchFilter,
  SearchFilterProps,
  Spinner,
  Table,
  TableCell,
  TableContent,
  TableProps,
} from '@squiz/sds';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { EntityJobContext } from '../../../../Services/job-runner';
import { useJobContexts } from '../../../../Services/job-runner/src/Hooks/UseJobContexts/UseJobContexts.hook';
import { JOB_CONTEXTS_DATA_IDS } from '../../JobContexts.constants';
import { JobContextForm } from '../../forms/JobContextForm/JobContextForm';
import { CreateJobContext } from '../CreateJobContext/CreateJobContext';

/**
 * The list of job contexts.
 * @export
 * @returns {JSX.Element} The `<JobContextsList />` component.
 */
export const JobContextsList = (): JSX.Element => {
  const { t } = useTranslation();

  const [cacheKey, setCacheKey] = useState<string>(``);
  const [contexts] = useJobContexts(cacheKey);
  const [filterValue, setFilterValue] = useState<string>(``);

  const { updateActiveDialog } = useDialogStore();

  // Transform all job contexts into array of table rows
  const allTableRows = useMemo(() => {
    if (!contexts) {
      return [];
    }

    return contexts
      .sort((a, b) => sortByProp(a, b, (item) => item.contextName.toLowerCase()))
      .map((jobContext: EntityJobContext): {} => {
        const { contextName, description } = jobContext;

        // The dialog props for the editing/updating an existing JobContext.
        const dialogProps: DialogProps = {
          classes: 'create-job-context__dialog',
          dialogContent: {
            renderContent: (dialogContentProps: DialogContentProps): JSX.Element => {
              return <JobContextForm {...dialogContentProps} clearCache={setCacheKey} jobContext={jobContext} />;
            },
          },
          dialogSize: DIALOG_SIZE_SM_MD,
          heading: contextName,
          scrollOnOverflow: true,
          showDividers: true,
          stateHandler: useDialogStore,
        };
        const openDialog = (): void => {
          updateActiveDialog(dialogProps);
        };

        const contextNameButton = (
          <Button
            className="job-context__name"
            dataId={JOB_CONTEXTS_DATA_IDS.contextNameButton}
            onClick={(): void => openDialog()}
            text={contextName}
          />
        );

        // Add `_keywords` prop to each context to enable keyword filtering
        const keywords = `${contextName} ${description}`;

        return {
          _config: {
            classes: `job-contexts__row`,
          },
          _keywords: keywords,
          contextName: contextNameButton,
          description: description ? description : '-',
        };
      });
  }, [contexts, updateActiveDialog]);

  const filteredRows = useMemo(() => {
    return filterByKeyword(allTableRows, filterValue);
  }, [allTableRows, filterValue]);

  // Labels
  const labels = useMemo(() => {
    return {
      description: t(CONFIGURATION_STRINGS.description),
      name: t(CONFIGURATION_STRINGS.name),
      noJobContextsCreated: t(CONFIGURATION_STRINGS.noJobContextsCreated),
      noMatchingJobContexts: t(CONFIGURATION_STRINGS.noMatchingJobContexts),
      searchContextsPlaceholder: t(CONFIGURATION_STRINGS.searchContextsPlaceholder),
    };
  }, [t]);

  const filterProps = {
    filterValue,
    placeholder: labels.searchContextsPlaceholder,
    setFilterValue,
  } as SearchFilterProps;

  const getJobContextsList = (): JSX.Element => {
    if (filteredRows && filteredRows.length === 0) {
      const emptyStateProps = {
        caption: labels.noMatchingJobContexts,
        svg: ICON_INFO_WITH_SEARCH,
      };
      return <EmptyState {...emptyStateProps} />;
    }

    // Table setup
    const tableColumns: Array<TableCell> = [
      {
        content: labels.name,
        key: `contextName`,
      },
      {
        content: labels.description,
        key: `description`,
      },
    ];

    const tableContent: TableContent = { columns: tableColumns, rows: filteredRows };
    const tableProps: TableProps = {
      classes: `job-contexts__table`,
      perPageDefault: 20,
      perPageOptions: [20, 50, 100],
      tableContent: tableContent,
    };

    return <Table {...tableProps} />;
  };

  if (contexts && contexts.length === 0) {
    const emptyStateProps = {
      caption: labels.noJobContextsCreated,
      children: <CreateJobContext clearCache={setCacheKey} />,
      classes: `job-contexts--empty`,
      svg: ICON_INFO_WITH_LIST,
    };
    return <EmptyState {...emptyStateProps} />;
  }

  if (contexts) {
    return (
      <>
        <div className="job-contexts-bar">
          <div className="job-contexts-bar__filter" data-id={JOB_CONTEXTS_DATA_IDS.searchBar}>
            <SearchFilter {...filterProps} />
          </div>
          <CreateJobContext clearCache={setCacheKey} />
        </div>
        {getJobContextsList()}
      </>
    );
  }

  return (
    <div className="loading">
      <Spinner type={SPINNER_TYPE_REGULAR} />
    </div>
  );
};
